import { useEffect, useMemo, useRef, useState } from "react";
import { useVestaView } from "./vestaStore";
import { BookOpen, ClipboardList, FileText, Warehouse, Loader2, Download, ChevronDown, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { COPPER } from "./VestaAuth";
import { getAs, useVestaMe, useAsId } from "./VestaViewAs";

const asParams = () => { const a = getAs(); return a ? { as: a.id } : {}; };
import { eur, perWp, pct } from "@/lib/price";
const dt = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
const kwp = (n) => (n ? `${Number(n).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kWp` : "");

const STATI = {
  in_attesa: ["In attesa", "#a3a3a3"], in_corso: ["In corso", "#60a5fa"], parziale: ["Parziale", "#f59e0b"], consegnata: ["Consegnata", "#22c55e"],
  Aperto: ["Aperto", "#60a5fa"], Bozza: ["Bozza", "#a3a3a3"], "Da Approvare": ["Da approvare", "#f59e0b"], Chiuso: ["Confermato", "#22c55e"], Rifiutato: ["Rifiutato", "#ef4444"],
  CONSEGNATO: ["Consegnato", "#22c55e"], DISPONIBILE: ["Disponibile", "#C17F59"], "IN ARRIVO": ["In arrivo", "#60a5fa"], "IN RITARDO": ["In ritardo", "#ef4444"],
  "NON DISP.": ["Non disponibile", "#ef4444"], IDENTIFICAZIONE: ["In preparazione", "#a3a3a3"], "IN VERIFICA": ["In verifica", "#a3a3a3"],
};
export const Badge = ({ s, testid }) => {
  const [l, c] = STATI[s] || [s || "—", "#a3a3a3"];
  return <span className="whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest" style={{ color: c, border: `1px solid ${c}66` }} data-testid={testid}>{l}</span>;
};

const useLoad = (open, url) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!open) return;
    setD(null); setErr("");
    api.get(url, { params: asParams() }).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Errore di caricamento"));
  }, [open, url]);
  return [d, err];
};

const openPdf = async (url, setBusy) => {
  setBusy?.(true);
  const w = window.open("", "_blank");
  try {
    const r = await api.get(url, { params: asParams(), responseType: "blob", timeout: 120000 });
    const u = URL.createObjectURL(r.data);
    if (w) w.location.href = u; else window.location.href = u;
  } catch (e) { w?.close(); toast.error(e.response?.status === 404 ? "PDF non disponibile" : "Download non riuscito"); }
  setBusy?.(false);
};

const PdfBtn = ({ url, testid, label = "PDF" }) => {
  const [busy, setBusy] = useState(false);
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); openPdf(url, setBusy); }} disabled={busy} data-testid={testid}
      className="inline-flex items-center gap-1 rounded-full border border-neutral-700 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-neutral-200 transition-colors hover:border-[#C17F59] hover:text-[#C17F59] disabled:opacity-50">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}{label}
    </button>
  );
};

const useNet = () => { const me = useVestaMe(); const asId = useAsId(); return !!me?.network && !asId; };
const Shell = ({ open, onOpenChange, title, icon: I, testid, children, actions }) => { const net = useNet(); return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid={testid}>
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center justify-between gap-3 pr-6 font-heading text-xl" style={{ color: COPPER }}>
          <span className="flex items-center gap-2"><I className="h-6 w-6" />{title}</span>{actions}
        </DialogTitle>
      </DialogHeader>
      <DialogDescription className="-mt-2 text-[11px] uppercase tracking-widest text-neutral-500" data-testid="vesta-bo-scope">{net ? "Vista rete · tutti i Partner · sola lettura" : "Sola lettura · dati della tua azienda"}</DialogDescription>
      {children}
    </DialogContent>
  </Dialog>
); };

const State = ({ d, err, empty, children }) => err ? <p className="text-sm text-red-400" data-testid="vesta-bo-error">{err}</p>
  : !d ? <Loader2 className="h-5 w-5 animate-spin" style={{ color: COPPER }} />
  : empty ? <p className="py-6 text-center text-sm text-neutral-500" data-testid="vesta-bo-empty">{empty}</p> : children;

const Righe = ({ righe }) => (
  <table className="w-full text-xs">
    <thead><tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500"><th className="py-1 pr-2">Articolo</th><th className="pr-2 text-right">Q.tà</th><th className="pr-2 text-right">Prezzo</th><th className="text-right">Totale</th></tr></thead>
    <tbody>{righe.map((r, i) => (
      <tr key={i} className="border-t border-neutral-900" data-testid={`vesta-bo-riga-${i}`}><td className="py-1.5 pr-2">{r.codice}{r.tipologia && <span className="ml-1 text-neutral-500">· {r.tipologia}</span>}
          {r.nome && r.nome !== r.codice && <div className="line-clamp-2 max-w-md text-[11px] text-neutral-400" title={r.nome} data-testid={`vesta-bo-riga-desc-${i}`}>{r.nome}</div>}</td>
        <td className="pr-2 text-right font-mono">{r.quantita} {r.um}</td>
        <td className="pr-2 text-right font-mono font-semibold">{eur(r.prezzo_unitario)}{perWp(r.prezzo_unitario, r) && <div className="text-[10px] font-normal text-neutral-500">{perWp(r.prezzo_unitario, r)}</div>}</td><td className="text-right font-mono">{eur(r.prezzo_totale)}</td></tr>
    ))}</tbody>
  </table>
);

function DocRow({ r, kind, pdfUrl, focus }) {
  const [open, setOpen] = useState(!!focus);
  const ref = useRef(null);
  useEffect(() => { if (focus) { setOpen(true); setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 250); } }, [focus]);
  useVestaView(open, { scheda: kind === "commesse" ? "commessa" : "preventivo", numero: r.numero, cliente: r.cliente, partner: r.partner, stato: r.stato, importo_eur: r.importo_eur });
  return (
    <div ref={ref} className={`rounded-xl border ${focus ? "border-[#C17F59] shadow-[0_0_24px_-6px_rgba(193,127,89,.7)]" : "border-neutral-800"}`} data-testid={`vesta-bo-${kind}-${r.id}`} data-focus={focus ? "1" : undefined}>
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 p-3 text-left" data-testid={`vesta-bo-${kind}-toggle-${r.id}`}>
        <span className="font-heading font-extrabold">N° {r.numero || "—"}</span>
        {r.partner && <span className="text-xs font-semibold" style={{ color: COPPER }} data-testid={`vesta-bo-${kind}-partner-${r.id}`}>{r.partner}</span>}
        <span className="text-xs text-neutral-400">{dt(r.data)}</span>
        {r.cliente && <span className="text-xs text-neutral-300">{r.cliente}</span>}
        <span className="text-xs text-neutral-400">{kwp(r.kwp)}</span>
        <span className="ml-auto flex items-center gap-3">
          <span className="font-mono text-sm" style={{ color: COPPER }}>{eur(r.importo_eur)}</span>
          <Badge s={r.stato} testid={`vesta-bo-${kind}-stato-${r.id}`} />
          <ChevronDown className={`h-4 w-4 text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-neutral-900 p-3">
          {r.righe.length ? <Righe righe={r.righe} /> : <p className="text-xs text-neutral-500">Dettaglio articoli non disponibile.</p>}
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-400">
            <span>{r.da_preventivo && <span className="mr-2 text-neutral-300" data-testid={`vesta-bo-${kind}-preventivo-${r.id}`}>Da preventivo N° {r.da_preventivo}{r.importo_preventivo_eur != null ? ` (preventivato ${eur(r.importo_preventivo_eur)})` : ""} ·</span>}{r.modalita_pagamento ? `Pagamento: ${r.modalita_pagamento}` : ""}{r.importo_con_iva_eur ? ` · IVA inclusa ${eur(r.importo_con_iva_eur)}` : ""}</span>
            {r.has_pdf ? <PdfBtn url={pdfUrl} testid={`vesta-bo-${kind}-pdf-${r.id}`} /> : <span className="text-[11px] text-neutral-600">PDF non disponibile</span>}
          </div>
        </div>
      )}
    </div>
  );
}

const Filter = ({ v, set, opts, testid }) => (
  <div className="flex gap-2 overflow-x-auto pb-1" data-testid={testid}>
    {opts.map(([k, l]) => (
      <button key={k} type="button" onClick={() => set(k)} data-testid={`${testid}-${k || "tutti"}`}
        className="whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold transition-colors"
        style={v === k ? { background: COPPER, borderColor: COPPER, color: "#000" } : { borderColor: "#262626", color: "#d4d4d4" }}>{l}</button>
    ))}
  </div>
);

export function ListinoDialog({ open, onOpenChange, focus }) {
  const [d, err] = useLoad(open, "/vesta/backoffice/listino");
  const net = useNet();
  const [q, setQ] = useState("");
  useEffect(() => { if (open && focus?.q) setQ(focus.q); }, [open, focus?.q]);
  useVestaView(open, { scheda: "listino commerciale", ricerca: q });
  const [cat, setCat] = useState("");
  const list = useMemo(() => (d?.products || []).filter((p) => (!cat || p.tipologia === cat) && (!q || `${p.codice} ${p.descrizione}`.toLowerCase().includes(q.toLowerCase()))), [d, q, cat]);
  return (
    <Shell open={open} onOpenChange={onOpenChange} title="Listino commerciale" icon={BookOpen} testid="vesta-bo-listino-dialog"
      actions={<PdfBtn url="/vesta/backoffice/listino/pdf" testid="vesta-bo-listino-pdf" label="Scarica PDF" />}>
      <State d={d} err={err}>
        <div className="space-y-3">
          {net && <p className="rounded-xl border border-neutral-800 px-3 py-2 text-xs text-neutral-300" data-testid="vesta-bo-listino-base">Listino base della rete: prezzo di listino e prezzo all'installatore. Scegli un Partner per vedere il suo listino riservato.</p>}
          {d?.livello > 0 && <p className="rounded-xl border px-3 py-2 text-xs font-semibold" style={{ borderColor: `${COPPER}66`, color: COPPER }} data-testid="vesta-bo-listino-extra">Listino riservato: prezzi con il tuo extra sconto già applicato</p>}
          <div className="flex items-center gap-2 rounded-full border border-neutral-800 px-3">
            <Search className="h-4 w-4 text-neutral-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice o descrizione…" className="h-9 w-full bg-transparent text-sm outline-none" data-testid="vesta-bo-listino-search" />
          </div>
          <Filter v={cat} set={setCat} opts={[["", "Tutte"], ...(d?.tipologie || []).map((t) => [t, t])]} testid="vesta-bo-listino-cat" />
          <div className="divide-y divide-neutral-900" data-testid="vesta-bo-listino-list">
            {list.map((p) => (
              <div key={p.id} className="flex items-center gap-3 py-2.5" data-testid={`vesta-bo-listino-row-${p.id}`}>
                <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">{p.immagine_url && <img src={p.immagine_url} alt="" className="h-full w-full object-contain" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />}</div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{p.codice}</div>
                  <div className="line-clamp-1 text-xs text-neutral-500" title={p.descrizione}>{p.tipologia} · {p.descrizione}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-mono text-[11px] text-neutral-400" data-testid={`vesta-bo-listino-listino-${p.id}`}>Listino <span className={p.sconto_pct > 0 ? "line-through" : ""}>{eur(p.prezzo_listino)}</span>{perWp(p.prezzo_listino, p) && <span> ({perWp(p.prezzo_listino, p)})</span>}</div>
                  <div className="font-mono text-[11px] font-semibold" style={{ color: COPPER }} data-testid={`vesta-bo-listino-sconto-${p.id}`}>Sconto {pct(p.sconto_pct)}</div>
                  <div className="font-mono text-sm font-bold" style={{ color: COPPER }} data-testid={`vesta-bo-listino-price-${p.id}`}>{eur(p.prezzo)}</div>
                  {perWp(p.prezzo, p) && <div className="font-mono text-[11px] font-semibold" style={{ color: COPPER }} data-testid={`vesta-bo-listino-wp-${p.id}`}>{perWp(p.prezzo, p)}</div>}
                  <div className="text-[10px] text-neutral-500">/{p.um || "pz"} + IVA</div></div>
              </div>
            ))}
            {list.length === 0 && <p className="py-6 text-center text-sm text-neutral-500">Nessun articolo trovato.</p>}
          </div>
        </div>
      </State>
    </Shell>
  );
}

function DocsDialog({ open, onOpenChange, kind, title, icon, url, key_, filters, focus }) {
  const [d, err] = useLoad(open, url);
  const [f, setF] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { if (open && focus) { setF(""); setQ(focus.q || ""); } }, [open, focus?.id, focus?.q]); // eslint-disable-line react-hooks/exhaustive-deps
  const qq = q.trim().toLowerCase();
  const all = (d?.[key_] || []).filter((r) => (!f || r.stato === f) && (!qq || [r.numero, r.cliente, r.partner].some((x) => (x || "").toLowerCase().includes(qq))));
  const net = all.some((r) => r.partner);
  const rows = all.slice(0, 200);
  return (
    <Shell open={open} onOpenChange={onOpenChange} title={title} icon={icon} testid={`vesta-bo-${kind}-dialog`}>
      <State d={d} err={err} empty={d && !d[key_].length ? `Nessun${kind === "commesse" ? "a commessa" : " preventivo"} presente.` : ""}>
        <div className="space-y-3">
          <Filter v={f} set={setF} opts={filters} testid={`vesta-bo-${kind}-filter`} />
          <div className="flex items-center gap-2 rounded-xl border border-neutral-800 px-3">
            <Search className="h-4 w-4 text-neutral-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca numero, cliente o Partner…" className="h-9 w-full bg-transparent text-sm outline-none" data-testid={`vesta-bo-${kind}-search`} />
          </div>
          {net && <p className="text-[11px] text-neutral-500" data-testid={`vesta-bo-${kind}-count`}>{all.length} documenti nella rete{all.length > rows.length ? ` · mostrati i primi ${rows.length}, usa la ricerca` : ""}</p>}
          {rows.map((r) => <DocRow key={r.id} r={r} kind={kind} pdfUrl={`${url}/${r.id}/pdf`} focus={focus?.id === r.id} />)}
        </div>
      </State>
    </Shell>
  );
}

export const CommesseDialog = (p) => <DocsDialog {...p} kind="commesse" title="Commesse" icon={ClipboardList} url="/vesta/backoffice/commesse" key_="commesse"
  filters={[["", "Tutte"], ["in_corso", "In corso"], ["parziale", "Parziali"], ["consegnata", "Consegnate"], ["in_attesa", "In attesa"]]} />;
export const PreventiviDialog = (p) => <DocsDialog {...p} kind="preventivi" title="Preventivi" icon={FileText} url="/vesta/backoffice/preventivi" key_="preventivi"
  filters={[["", "Tutti"], ["Aperto", "Aperti"], ["Da Approvare", "Da approvare"], ["Chiuso", "Confermati"], ["Rifiutato", "Rifiutati"]]} />;

const SEM = { verde: ["#22c55e", "Disponibile"], giallo: ["#f59e0b", "In arrivo"], rosso: ["#ef4444", "Esaurito"], richiesta: ["#a3a3a3", "Su richiesta"] };
const nInt = (n) => Number(n || 0).toLocaleString("it-IT");
const meta = (iso) => { const d = new Date(iso); return `entro la ${d.getDate() <= 15 ? "prima" : "seconda"} metà di ${d.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}`; };

export function MagazzinoDialog({ open, onOpenChange }) {
  const [d, err] = useLoad(open, "/vesta/backoffice/magazzino");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [sem, setSem] = useState("");
  const list = useMemo(() => (d?.articoli || []).filter((p) => (!cat || p.tipologia === cat) && (!sem || p.semaforo === sem)
    && (!q || `${p.codice} ${p.descrizione}`.toLowerCase().includes(q.toLowerCase()))), [d, q, cat, sem]);
  return (
    <Shell open={open} onOpenChange={onOpenChange} title="Magazzino" icon={Warehouse} testid="vesta-bo-magazzino-dialog"
      actions={d?.aggiornato_al && <span className="text-[11px] font-normal text-neutral-400" data-testid="vesta-bo-mag-updated">Aggiornato {new Date(d.aggiornato_al).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span>}>
      <State d={d} err={err}>
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-full border border-neutral-800 px-3">
            <Search className="h-4 w-4 text-neutral-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice o descrizione…" className="h-9 w-full bg-transparent text-sm outline-none" data-testid="vesta-bo-mag-search" />
          </div>
          <Filter v={cat} set={setCat} opts={[["", "Tutte"], ...(d?.tipologie || []).map((t) => [t, t])]} testid="vesta-bo-mag-cat" />
          <Filter v={sem} set={setSem} opts={[["", "Tutti gli stati"], ["verde", "Disponibili"], ["giallo", "In arrivo"], ["rosso", "Esauriti"], ["richiesta", "Su richiesta"]]} testid="vesta-bo-mag-sem" />
          <div className="divide-y divide-neutral-900" data-testid="vesta-bo-mag-list">
            {list.map((p) => {
              const [c, l] = SEM[p.semaforo];
              const a = p.prossimo_arrivo;
              return (
                <div key={p.id} className="flex items-center gap-3 py-2.5" data-testid={`vesta-bo-mag-row-${p.id}`}>
                  <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: c, boxShadow: `0 0 10px ${c}` }} title={l} data-testid={`vesta-bo-mag-sem-${p.id}`} />
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">{p.immagine_url && <img src={p.immagine_url} alt="" className="h-full w-full object-contain" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />}</div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{p.codice}</div>
                    <div className="line-clamp-1 text-xs text-neutral-500" title={p.descrizione}>{p.tipologia}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    {p.su_richiesta ? <div className="font-mono text-sm font-bold text-neutral-300" data-testid={`vesta-bo-mag-disp-${p.id}`}>Su richiesta</div> : <>
                    <div className="font-mono text-sm font-bold" style={{ color: c }} data-testid={`vesta-bo-mag-disp-${p.id}`}>{nInt(p.disponibile)} <span className="text-[10px] font-normal text-neutral-500">{p.um || "pz"}</span></div>
                    <div className="text-[10px] text-neutral-400" data-testid={`vesta-bo-mag-arrivo-${p.id}`}>
                      {a ? (/^moduli/i.test(p.tipologia || "") ? <>previsione di approntamento: {meta(a.data)}</> : <>arrivo {dt(a.data)} · {nInt(a.qta)}</>) : (/^moduli/i.test(p.tipologia || "") ? "nessuna previsione di approntamento" : "nessun arrivo previsto")}
                      {a?.in_ritardo && <span className="text-red-400"> · in ritardo</span>}
                    </div></>}
                  </div>
                </div>
              );
            })}
            {list.length === 0 && <p className="py-6 text-center text-sm text-neutral-500">Nessun articolo trovato.</p>}
          </div>
          <p className="text-[11px] text-neutral-500">Disponibile = giacenza meno quantità già impegnate su altri ordini. Le date di arrivo sono indicative.</p>
        </div>
      </State>
    </Shell>
  );
}
